import { Reveal } from "./Reveal";
import { renderAA } from "./aa";
import { Mail, Phone, Linkedin, Twitter, Youtube, Github, MessagesSquare, Heart } from "lucide-react";

export const Continuation = ({ onContact }) => (
  <section className="relative z-[91] bg-[#F7F5F0] py-10 lg:py-12" data-testid="cta-banner-section">
    <div className="max-w-7xl mx-auto px-6 sm:px-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
      <Reveal>
        <h2 className="font-display font-medium tracking-tight text-2xl sm:text-3xl lg:text-[2.5rem] leading-[1.12] text-[#3E2818] max-w-xl">
          Accelerate your Agentic Enterprise Journey
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <button
          data-testid="cta-talk-to-experts-btn"
          onClick={() => onContact("Talk to our experts")}
          className="btn-primary shrink-0"
        >
          Talk to our experts
        </button>
      </Reveal>
    </div>
  </section>
);

const RESOURCES = [
  { label: "Documentation", href: "https://intugle.github.io/data-tools/" },
  { label: "Compliance & Trust Center", href: "https://trust.intugle.ai/" },
  { label: "Privacy Policy", href: "https://intugle.ai/privacy" },
  { label: "Terms of Service", href: "https://intugle.ai/terms" },
];
const COMMUNITY = [
  { label: "GitHub", href: "https://github.com/Intugle/data-tools" },
  { label: "Discord Community", href: "https://discord.com/invite/NqR9tNWVTm" },
  { label: "MCP Server", href: "https://mcpservers.org/servers/intugle/data-tools" },
];
const CONTACTS = [
  { icon: Mail, label: "hello@intugle.ai", href: "mailto:hello@intugle.ai" },
  { icon: Phone, label: "+1 (415) 237-9656 (US)", href: "tel:+14152379656" },
  { icon: Phone, label: "+91 7829811923 (India)", href: "tel:+917829811923" },
  { icon: Phone, label: "+1 (368) 993-2225 (Canada)", href: "tel:+13689932225" },
];
const SOCIALS = [
  { icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com/company/intugle" },
  { icon: Twitter, label: "X", href: "https://x.com/intugle" },
  { icon: Youtube, label: "YouTube", href: "https://www.youtube.com/@Intugle" },
  { icon: MessagesSquare, label: "Discord", href: "https://discord.com/invite/NqR9tNWVTm" },
  { icon: Github, label: "GitHub", href: "https://github.com/Intugle/data-tools" },
];

const linkCls = "text-sm text-[#F7F5F0]/55 hover:text-[#F7F5F0] transition-colors";
const headCls = "font-display text-lg font-semibold text-[#F7F5F0] mb-5";

export const Footer = () => (
  <footer className="relative z-[91] bg-[#211307] py-16" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
        {/* Brand */}
        <div className="lg:pr-6">
          <img
            src="https://customer-assets-rejwkqb3.emergentagent.net/job_intugle-ai-platform/artifacts/bziz90bu_intugle_logo_H_white_cropped.webp"
            alt="Intugle"
            className="h-14 w-auto"
          />
          <p className="mt-3 font-display italic text-[#F7F5F0]/55">Unlock {renderAA("Agentic Alpha")}.</p>
          <p className="mt-5 text-sm text-[#F7F5F0]/45 flex items-center gap-1.5 flex-wrap">
            Made with <Heart size={13} className="fill-[#D1633D] text-[#D1633D]" /> in San Francisco, Calgary and Bengaluru
          </p>
          <div className="mt-6 flex items-center gap-3">
            {["SOC 2 · Type II", "ISO 27001"].map((b) => (
              <span key={b} className="inline-flex items-center rounded-full border border-white/15 bg-white/[0.04] px-3 py-1 font-mono2 text-[10px] tracking-wide text-[#F7F5F0]/65">
                {b}
              </span>
            ))}
          </div>
        </div>

        {/* Resources + Community */}
        <div data-testid="footer-resources">
          <h3 className={headCls}>Resources</h3>
          <ul className="space-y-3">
            {RESOURCES.map((r) => (
              <li key={r.label}><a href={r.href} target="_blank" rel="noopener noreferrer" data-testid={`footer-resource-${r.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`} className={linkCls}>{r.label}</a></li>
            ))}
          </ul>
          <h3 className={`${headCls} mt-8`}>Community</h3>
          <ul className="space-y-3">
            {COMMUNITY.map((c) => (
              <li key={c.label}><a href={c.href} target="_blank" rel="noopener noreferrer" data-testid={`footer-community-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`} className={linkCls}>{c.label}</a></li>
            ))}
          </ul>
        </div>

        {/* Contact + Follow */}
        <div className="lg:col-start-4" data-testid="footer-contact">
          <h3 className={headCls}>Contact Us</h3>
          <ul className="space-y-3.5">
            {CONTACTS.map((c) => (
              <li key={c.label}>
                <a href={c.href} className={`${linkCls} flex items-center gap-2.5`}>
                  <c.icon size={15} className="text-[#D1633D] shrink-0" strokeWidth={1.8} />
                  {c.label}
                </a>
              </li>
            ))}
          </ul>
          <h3 className={`${headCls} mt-8`}>Follow Us</h3>
          <div className="flex items-center gap-3">
            {SOCIALS.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                data-testid={`footer-social-${s.label.toLowerCase()}`}
                className="w-9 h-9 rounded-lg bg-white/[0.07] flex items-center justify-center text-[#F7F5F0]/70 hover:text-white hover:bg-[#D1633D] transition-colors"
              >
                <s.icon size={16} strokeWidth={1.8} />
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="font-mono2 text-xs text-[#F7F5F0]/35">© 2026 Intugle. All rights reserved.</p>
        <p className="font-mono2 text-xs text-[#F7F5F0]/35">Enterprise Agentic AI</p>
      </div>
    </div>
  </footer>
);
